import { createGqlFetcher } from 'app/lib/gqlFetcher'
import { ArrowPathIcon } from '@heroicons/react/20/solid'
import { withZod } from '@remix-validated-form/with-zod'
import classNames from 'classnames'
import { useEffect, useState } from 'react'
import { z } from 'zod'
import { AddNewIntegrationModal } from '../AddNewIntegrationModal'
import { CALL_RAIL_COMPANIES_QUERY } from '../graphql'
import { SAVE_CALLRAIL_INTEGRATION, REMOVE_LOCATION_INTEGRATION } from '../ops-graphql'
import { useIntegrationQuery } from '../useIntegrationQuery'
import Button from 'app/components/Button'
import { useConfirmation } from 'app/components/ConfirmationContext'
import FormCombo from 'app/components/form/FormCombo'
import FormComboAsync from 'app/components/form/FormComboAsync'
import FormDatePicker from 'app/components/form/FormDatePicker'
import FormSelect from 'app/components/form/FormComboSelect'
import { SmartForm } from 'app/components/form/SmartForm'
import Spinner from 'app/components/Spinner'
import type { CallRailCompany } from 'app/generated/graphql'
import type { LocationJson, LocationType } from 'app/types/entities'
import { closeModal, showModal } from 'app/lib/modal'
import { gqlOpsMutation } from 'app/lib/opsGraphqlClient'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import moment from 'moment'
import { OptionType } from 'types/OptionType'

const createSchema = z.object({
  locationId: z.string().min(1, { message: `A Location is required` }),
  companyId: z.string().min(1, { message: `A Company is required` }),
  backfillStartDate: z.string().optional(),
})

export const createCallRailValidator = withZod(createSchema)

type CallRailCompaniesQueryResult = {
  integrationOptions: {
    callRailCompanies: CallRailCompany[]
  }
}

export function CallRailFormModal({
  onAdd,
  currentValues,
}: {
  onAdd: () => void
  currentValues?: { locationId: string; locationName: string; callRailCompanyId: LocationType[`callRailCompanyId`] }
}) {
  const [selectedLocation, setSelectedLocation] = useState<OptionType | undefined>()
  const [backfillStartDate, setBackfillStartDate] = useState<string>(moment().subtract(2, `years`).format(`YYYY-MM-DD`))
  const [selectedOption, setSelectedOption] = useState<OptionType | undefined>(undefined)
  const [confirm] = useConfirmation()

  const { options, fetching, error, refreshing, refresh } = useIntegrationQuery<
    CallRailCompaniesQueryResult,
    CallRailCompany
  >({
    query: CALL_RAIL_COMPANIES_QUERY,
    variables: { locationId: currentValues?.locationId },
    extractItems: (data) => data.integrationOptions.callRailCompanies,
    mapToOption: (company) => ({
      id: company.id,
      name: company.name,
      value: company.id,
      meta: {
        isUsed: company.isUsed,
        tooltipForDisabledOption: `This company is connected to another location`,
        type: `company`,
      },
    }),
    errorMessage: `Failed to load CallRail options`,
  })

  // Set selected option when data loads and we have current values
  useEffect(() => {
    if (options.length && currentValues?.callRailCompanyId) {
      const opt = options.find((o) => o.id === currentValues.callRailCompanyId)
      if (opt) setSelectedOption(opt)
    }
  }, [options, currentValues?.callRailCompanyId])

  const onSave = async () => {
    const locationId = currentValues?.locationId || (selectedLocation?.value as string)
    const companyId = selectedOption?.id
    if (!locationId || !companyId) return

    const result = await gqlOpsMutation(SAVE_CALLRAIL_INTEGRATION, {
      locationId,
      companyId,
      backfillStartDate,
      isNew: !currentValues,
    })
    const data = result.data?.saveCallRailIntegration
    if (data?.success) {
      notifySuccess(`CallRail integration saved`)
      onAdd()
      closeModal()
    } else {
      notifyError(`Failed to save integration: ${data?.error || result.error || 'Unknown error'}`)
    }
  }

  const goBackToSelectIntegrationModal = () => {
    closeModal()
    showModal(
      <AddNewIntegrationModal onIntegrationAdded={onAdd} />,
      {
        title: `Add New Integration`,
        isFullScreen: false,
        overflowHidden: false,
        removePadding: true,
        useRadix: true,
      },
      false,
    )
  }

  const onRemove = () => {
    return confirm({
      title: `Remove CallRail Integration`,
      description: `Removing this integration will also remove all associated data. This may result in contacts being removed. Do you want to continue?`,
      variant: `danger`,
    }).then(() =>
      gqlOpsMutation(REMOVE_LOCATION_INTEGRATION, {
        locationId: currentValues?.locationId!,
        integration: `callrail`,
      }).then((result) => {
        if (result.data?.removeLocationIntegration?.success) {
          notifySuccess(`CallRail integration removed`)
          onAdd()
          closeModal()
        } else {
          notifyError(`Failed to remove integration: ${result.error || 'Unknown error'}`)
        }
      }),
    )
  }

  return (
    <>
      <SmartForm
        id="integration-form"
        className="!mt-0 w-[600px] grow-0 justify-start !gap-y-4 px-6 py-4"
        validator={createCallRailValidator}
        reloadDocument={false}
        preventSubmit={true}
        onSubmit={onSave}
        hideButtons={true}
        method={currentValues ? `PUT` : `POST`}
        hiddenInputs={currentValues ? [{ name: `locationId`, value: currentValues.locationId }] : undefined}
      >
        {currentValues ? (
          <FormSelect
            cols={5}
            name="locationId"
            label="Trackable Location"
            options={[{ label: currentValues.locationName, value: currentValues.locationId }]}
            value={currentValues.locationId}
            disabled
            labelClassName="text-body-sm text-strong"
          />
        ) : (
          <FormComboAsync
            cols={5}
            name="locationId"
            required={true}
            showSearchInDropdown={true}
            label="Trackable Location"
            value={selectedLocation}
            optionsMapper={(res) =>
              res?.hits?.map((hit: LocationJson) => ({
                id: hit.id || hit.name,
                name: hit.name,
                value: hit.id,
                meta: {
                  isUsed: !!hit.callRailCompanyId,
                  tooltipForDisabledOption: `This location is already connected to CallRail`,
                },
              }))
            }
            fetcher={createGqlFetcher('locations')}
            labelClassName="text-body-sm text-strong"
            onChange={(val) => setSelectedLocation(val)}
            newLineError={false}
            showErrorIcon
          />
        )}
        {fetching ? (
          <div className="col-span-5 flex items-center pt-5">
            <Spinner size={5} />
          </div>
        ) : error ? (
          <div className="col-span-5 pt-5 text-sm text-red-600">Failed to load companies</div>
        ) : options.length === 0 ? (
          <div className="col-span-5 pt-5 text-sm text-subtle">No companies available</div>
        ) : (
          <FormCombo
            cols={5}
            name="companyId"
            label="Company"
            value={selectedOption}
            options={options}
            labelClassName="text-body-sm text-strong"
            onChange={(opt) => setSelectedOption(opt)}
            showSearchInDropdown={true}
            newLineError={false}
            showErrorIcon
          />
        )}
        <div className="col-span-1 flex items-center justify-center pt-5">
          <ArrowPathIcon
            className={classNames(`ml-2 h-5 w-5 cursor-pointer`, refreshing && `animate-spin`)}
            onClick={refresh}
          />
        </div>
        {!currentValues && (
          <FormDatePicker
            cols={6}
            name="backfillStartDate"
            label="Backfill Start Date"
            value={backfillStartDate}
            onChange={(val) => setBackfillStartDate(val)}
            labelClassName="text-body-sm text-strong"
            allowFutureDates={false}
            minDate={moment().subtract(2, `years`).toDate()}
          />
        )}
      </SmartForm>
      <div className="sticky bottom-0 w-full rounded-b-xl border-t border-default bg-surface-raised px-6 py-3">
        <div className="flex items-center justify-between">
          <div>
            {currentValues ? <Button label="Remove" mode="danger" size="md" onClick={() => onRemove()} /> : null}
          </div>
          <div className="flex gap-2">
            {!currentValues ? (
              <Button label="Back" mode="white" size="md" onClick={() => goBackToSelectIntegrationModal()} />
            ) : (
              <Button label="Cancel" mode="white" size="md" onClick={() => closeModal()} />
            )}
            <Button
              type="submit"
              form="integration-form"
              label="Save"
              mode="primary"
              size="md"
              disabled={(!selectedLocation && !currentValues?.locationId) || !selectedOption}
            />
          </div>
        </div>
      </div>
    </>
  )
}
